import apis from '@apis/index';
import { Button, Divider, Stack } from '@chakra-ui/react';
import Content from '@components/content';
import OrderStatement from '@containers/checkout/components/order-statement';
import RequestDelivery from '@containers/checkout/components/request-delivery';
import Address from '@containers/checkout/components/address';
import CalculateDiscount from '@containers/checkout/components/caluclate-discount';
import useCart from '@hooks/useCart';
import useOrder from '@hooks/useOrder';
import usePopup from '@hooks/usePopup';
import { storeAddress } from '@stores/address';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { fetchBusinessTime } from './cart';
import AdditionalDeliveryFeeMethod from './components/additional-delivery-fee-method';
import Phone from './components/phone';
import NightOrderAgreeModal from './popups/night-order-agree.modal';
import { debounce } from 'utils/common';
import CheckAddressValidModal from './popups/check-address-image.modal';
import ROUTER from '@constants/router';
import CouponApplication from '@containers/checkout/components/coupon-application';
import { TOrderCreate } from '@@types/order';
import { checkStoreOpenStatus } from './queries';

export default function PaymentContainer() {
  const ref = useRef<HTMLDivElement | null>(null);
  const { status } = useSession();
  const { t } = useTranslation('store');
  const { onSubmit, validateOrder, totalPrice, updateOrderInfo, isOrdering } =
    useOrder();
  const { openAlert, openPromisePopup, closeAllPopup } = usePopup();
  const { cart } = useCart();

  const router = useRouter();
  const dispatch = useDispatch();
  const handleFocus = (section: string) => {
    if (ref.current) {
      const node = ref.current;
      const { children } = node;

      if (!children) return;
      Array.from(children).forEach((childNode) => {
        if (childNode.nodeName === 'DIV') {
          const _section = ((childNode as any).dataset as { section: string })
            .section;
          if (section === _section) {
            childNode.scrollIntoView({ behavior: 'smooth' });
          }
        }
      });
    }
  };

  const onSubmitValid = debounce(async () => {
    const validate = validateOrder(totalPrice);
    if (validate.state === 'fail') {
      await openAlert({
        description: validate.message,
        cancelButtonText: '' + t('common:close'),
        isConfirmButtonShow: false,
      });

      setTimeout(() => {
        if (validate.section) {
          handleFocus(validate.section);
        }
      });

      return;
    }
    if (totalPrice < 0) {
      router.reload();
    }

    const isOperationTime = await fetchBusinessTime();

    if (!isOperationTime?.data?.open) {
      const result = await openPromisePopup({
        isOpen: true,
        type: 'promiseModal',
        props: {},
        Component: NightOrderAgreeModal,
      });

      if (!result) return;
    }

    if (validate.data) {
      if (validate.data.address?._id) {
        const { data } = await apis.address.checkRoadviewImage(
          validate.data.address._id,
        );
        if (data.code === 200 && data.data?.roadviewImage) {
          const roadviewResult = await openPromisePopup<
            'promiseDrawer',
            'Y' | 'N'
          >({
            isOpen: true,
            props: { roadviewImage: data.data?.roadviewImage },
            type: 'promiseDrawer',
            popupOptions: { size: '3xl', placement: 'bottom' },
            Component: CheckAddressValidModal,
          });

          if (!roadviewResult) return;

          if (roadviewResult === 'N') {
            closeAllPopup();
            router.replace({ pathname: ROUTER.address });
            return;
          }
        }
      }

      // if (validate.data.address?.address) {
      //   const { address } = validate.data.address;
      //   const [mainAddress] = address.split(' ');

      //   if (
      //     mainAddress.includes('경기') ||
      //     mainAddress.includes('서울') ||
      //     mainAddress.includes('서울특별시') ||
      //     mainAddress.includes('인천') ||
      //     mainAddress.includes('충남') ||
      //     mainAddress.includes('충북') ||
      //     mainAddress.includes('충청남도') ||
      //     mainAddress.includes('충청북도')
      //   ) {
      //     await openAlert({
      //       description: '' + t('alert:bad_weather'),
      //       isCancelButtonShow: false,
      //       confirmButtonText: '' + t('common:ok'),
      //     });
      //   }
      // }

      await onSubmit(validate.data as TOrderCreate);
      const { address: currentAddress } =
        await apis.address.fetchCurrentAddress();
      dispatch(
        storeAddress({
          address: [],
          currentAddress,
        }),
      );
    }
  }, 100);

  const closePointDesc = (onClose: () => void) => {
    onClose();
  };

  useEffect(() => {
    updateOrderInfo({ cart });
    handleFocus('address');
  }, []);

  return (
    <>
      <Stack spacing="" divider={<Divider borderTopWidth={12} />} ref={ref}>
        <Content className="w-full" data-section="address">
          <div className="py-[8px]">
            <Address />
          </div>
        </Content>
        <Content className="w-full" data-section="phone">
          <div className="py-8">
            <Phone />
          </div>
        </Content>
        <Content className="w-full overflow-visible" data-section="message">
          <div className="py-8">
            <RequestDelivery />
          </div>
        </Content>
        <Content
          className="w-full overflow-visible"
          data-section="addDeliveryFeeMethod"
        >
          <div className="py-8">
            <AdditionalDeliveryFeeMethod />
          </div>
        </Content>

        {status === 'authenticated' && (
          <Content className="w-full" data-section="point">
            <div className="py-[8px]">
              <CalculateDiscount />
            </div>
          </Content>
        )}
        {status === 'authenticated' && (
          <Content className="w-full">
            <div className="py-[8px]">
              <CouponApplication />
            </div>
          </Content>
        )}

        <div className="my-[18px]">
          <OrderStatement />
          <div>
            <Content>
              <Button
                className="w-full mb-[16px] bg-primary-700 text-white"
                onClick={onSubmitValid}
                isLoading={isOrdering}
              >
                {t('payment_button')}
              </Button>
            </Content>
          </div>
        </div>
      </Stack>
    </>
  );
}
